'use client';

import { useMemo } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useUserSession } from '../../lib/context/UserSessionContext';
import { useNewChat } from '@/app/lib/context/NewChatContext';
import { useChat } from '@/app/lib/context/ChatContext';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface NavItem {
    label: string;
    /** Full href including any /ai prefix and optional query string. */
    href: string;
    /**
     * Optional explicit match path (path-only, no query string) used for
     * active-detection when the href contains a query string or differs from
     * the actual URL structure. Defaults to the pathname portion of `href`.
     */
    matchPath?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Strip the /ai prefix (legacy URL scheme), trailing slashes, and any query
 * string so we get a clean pathname to match against `usePathname()`.
 */
function normalizePath(raw: string): string {
    if (!raw) return '/';
    // Remove query string and hash
    const pathOnly = raw.split('?')[0].split('#')[0];
    return (
        pathOnly
            .replace(/^\/ai/, '')   // strip legacy /ai prefix
            .replace(/\/+$/, '')    // strip trailing slashes
        || '/'
    );
}

/**
 * Returns true when the current pathname matches this nav item.
 * We use the item's matchPath (or derived path from href) for comparison.
 * Longest specific match wins, so /supplier/products beats /supplier.
 */
function getActiveHref(
    normPathname: string,
    navItems: NavItem[],
): string {
    let activeHref = '';
    let activeLen = -1;

    for (const { href, matchPath } of navItems) {
        const candidate = normalizePath(matchPath ?? href);
        const isExact = normPathname === candidate;
        const isPrefix = normPathname.startsWith(candidate + '/');

        if ((isExact || isPrefix) && candidate.length > activeLen) {
            activeLen = candidate.length;
            activeHref = href;
        }
    }

    return activeHref;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function Nav() {
    const pathname = usePathname();
    const { sessionInfo, isLoadingSession } = useUserSession();
    const currentUserRole = sessionInfo?.session?.role;
    const { handleOpenChat } = useNewChat();
    const { handleSetOpenChat } = useChat();

    const navItemsByRole = useMemo<Record<string, NavItem[]>>(
        () => ({
            designer: [
                { label: 'HOME', href: '/designer' },
                { label: 'ROOMS', href: '/myrooms?userType=designer', matchPath: '/myrooms' },
                { label: 'DESIGNS', href: '/designer/designs' },
                { label: 'CAMPAIGNS', href: '/campaigns', matchPath: '/campaigns' },
            ],
            supplier: [
                { label: 'HOME', href: '/supplier' },
                {
                    label: 'PRODUCTS',
                    href: '/supplier/products',
                    // Explicit matchPath ensures sub-pages like /ai/supplier/products/abc123 are covered
                    matchPath: '/supplier/products',
                },
                {
                    label: 'TOOLS',
                    href: currentUserRole === 'admin'
                        ? '/tools'
                        : '/tools/supplier-product-feed-management',
                },
                { label: 'CAMPAIGNS', href: '/campaigns', matchPath: '/campaigns' },
            ],
            default: [
                { label: 'HOME', href: '/' },
                {
                    label: 'TOOLS',
                    href: currentUserRole === 'admin'
                        ? '/tools'
                        : '/tools/supplier-product-feed-management',
                },
            ],
        }),
        [currentUserRole],
    );

    const role = sessionInfo?.userType ?? 'default';
    const navItems = navItemsByRole[role] ?? navItemsByRole.default;

    // Skeleton while session is loading
    if (isLoadingSession) {
        return (
            <nav aria-label="Primary navigation" className="flex ml-[30px] items-end gap-5">
                {Array.from({ length: 4 }).map((_, i) => (
                    <div
                        key={i}
                        aria-hidden="true"
                        className="h-6 w-20 bg-gray-300 animate-pulse rounded mb-0.5"
                    />
                ))}
            </nav>
        );
    }

    const normPathname = normalizePath(pathname);
    const activeHref = getActiveHref(normPathname, navItems);

    return (
        <nav aria-label="Primary navigation" className="flex ml-[30px] items-end gap-5 pb-0.5">
            {navItems.map(({ label, href }) => {
                const isActive = href === activeHref;
                return (
                    <Link
                        key={href}
                        href={href}
                        onClick={() => {
                            handleOpenChat(false, '');
                            handleSetOpenChat({ isOpen: false, from: '' });
                        }}
                        className={`text-[14px] transition-colors ${isActive ? 'text-[#FE6526]' : 'text-inherit hover:text-[#FE6526]'}`}
                        aria-current={isActive ? 'page' : undefined}
                        prefetch={false}
                    >
                        {label}
                    </Link>
                );
            })}
        </nav>
    );
}
